import type { DriveStep } from "driver.js";
import { ROUTES } from "./routes";

const nav = (path: string) => `[data-tour="${path}"]`;

const ACTIONS = '[data-tour="page-actions"]';
const FILTERS = '[data-tour="page-filters"]';
const TABLE = '[data-tour="page-table"]';
const SUMMARY = '[data-tour="page-summary"]';

export const WELCOME_TOUR: DriveStep[] = [
  {
    popover: {
      title: "Le damos la bienvenida a Palomma",
      description:
        "Este recorrido muestra en 30 segundos dónde está cada sección del portal. Al entrar a cada una encontrará una guía de uso.",
    },
  },
  {
    element: nav(ROUTES.DASHBOARD.HOME),
    popover: {
      title: "Resumen",
      description:
        "Cuánto ha cobrado, cuánto está pendiente y cómo pagan los inquilinos.",
    },
  },
  {
    element: nav(ROUTES.DASHBOARD.CUSTOMERS),
    popover: {
      title: "Clientes",
      description:
        "Los inquilinos y los contratos que tienen con tu comercio. Se crean automáticamente con cada recaudo; aquí se consultan y corrigen los datos.",
    },
  },
  {
    element: nav(ROUTES.DASHBOARD.INVOICES),
    popover: {
      title: "Recaudos",
      description:
        "Las facturas que les cobra a los inquilinos y el estado de cada pago.",
    },
  },
  {
    element: nav(ROUTES.DASHBOARD.SETTLEMENTS),
    popover: {
      title: "Conciliación",
      description: "El detalle de cada abono y las facturas que lo componen.",
    },
  },
  {
    element: nav(ROUTES.DASHBOARD.WHATSAPP),
    popover: {
      title: "WhatsApp",
      description:
        "Configure los recordatorios de pago que se envían por WhatsApp a los inquilinos.",
    },
  },
  {
    element: nav(ROUTES.DASHBOARD.SUBSCRIPTIONS),
    popover: {
      title: "Suscripciones",
      description:
        "Palomma Plus: las suscripciones de los inquilinos y los beneficios que recibe el comercio por cada una.",
    },
  },
  {
    element: nav(ROUTES.DASHBOARD.PAYOUTS),
    popover: {
      title: "Pagos",
      description:
        "Gire a los propietarios el dinero recaudado y controle el saldo disponible.",
    },
  },
  {
    element: nav(ROUTES.DASHBOARD.BILLING),
    popover: {
      title: "Facturación",
      description:
        "Lo que Palomma cobra por el servicio, mes a mes, y el pago de cada cuenta.",
    },
  },
  {
    element: '[data-tour="tutorials"]',
    popover: {
      title: "Tutoriales",
      description:
        "Videos paso a paso de todo el portal. Ante cualquier duda, vuelva aquí.",
    },
  },
  {
    element: '[data-tour="copyPaymentsLink"]',
    popover: {
      title: "Enlace de pagos",
      description:
        "Copie aquí el enlace de pagos de su comercio: compártalo o configúrelo en su sitio web para que los inquilinos paguen en línea.",
    },
  },
  {
    element: '[data-tour="settings"]',
    popover: {
      title: "Configuración",
      description:
        "Logo e imagen del portal, datos de soporte, métodos de pago activos y cambios de la cuenta bancaria. Solo para administradores.",
    },
  },
  {
    element: '[data-tour="checklist"]',
    popover: {
      title: "Pasos pendientes",
      description:
        "Antes de empezar, revise esta lista: son los últimos pasos para completar la configuración de su comercio.",
    },
  },
];

/** Shown the first time a merchant admin opens the settings modal. */
export const SETTINGS_TOUR: DriveStep[] = [
  {
    popover: {
      title: "Configuración del comercio",
      description:
        "Aquí se ajusta lo que ven los inquilinos y cómo opera el comercio: imagen, soporte, métodos de pago y cuenta bancaria.",
    },
  },
  {
    element: '[data-tour="settings-tab-general"]',
    popover: {
      title: "General",
      description:
        "Logo e imagen de ingreso del portal, y el correo y WhatsApp de soporte que ven los inquilinos.",
    },
  },
  {
    element: '[data-tour="settings-tab-metodos"]',
    popover: {
      title: "Métodos de pago",
      description:
        "Consulte las comisiones de PSE y tarjeta, y active o desactive Nequi, Botón Bancolombia y Bre-B.",
    },
  },
  {
    element: '[data-tour="settings-tab-cuenta"]',
    popover: {
      title: "Cuenta bancaria",
      description:
        "Proponga un cambio de la cuenta donde recibe los abonos. Palomma lo revisa y aprueba antes de aplicarlo.",
    },
  },
  {
    element: '[data-tour="settings-tab-usuarios"]',
    popover: {
      title: "Usuarios",
      description:
        "Administre los usuarios que tienen acceso al portal y sus roles.",
    },
  },
];

/** Section tours, keyed by the exact route they belong to. */
export const PAGE_TOUR_BY_PATH: Record<string, string> = {
  [ROUTES.DASHBOARD.CUSTOMERS]: "clientes",
  [ROUTES.DASHBOARD.INVOICES]: "recaudos",
  [ROUTES.DASHBOARD.SETTLEMENTS]: "conciliacion",
  [ROUTES.DASHBOARD.WHATSAPP]: "whatsapp",
  [ROUTES.DASHBOARD.PAYOUTS]: "pagos",
  [ROUTES.DASHBOARD.BILLING]: "facturacion",
  [ROUTES.DASHBOARD.SUBSCRIPTIONS]: "suscripciones",
};

/** Shown the first time a user opens each section. */
export const PAGE_TOURS: Record<string, DriveStep[]> = {
  clientes: [
    {
      popover: {
        title: "Clientes",
        description:
          "Los datos de contacto de los inquilinos y los contratos que tienen con tu comercio.",
      },
    },
    {
      element: FILTERS,
      popover: {
        title: "Encuéntrelos rápido",
        description:
          "Busque por cédula o WhatsApp cuando la lista empiece a crecer.",
      },
    },
    {
      element: TABLE,
      popover: {
        title: "Lista de inquilinos",
        description:
          "El estado y los datos de cada inquilino. Use el lápiz de cada fila para editar la información.",
      },
    },
  ],
  recaudos: [
    {
      popover: {
        title: "Recaudos",
        description:
          "Cada factura que se le cobra a un inquilino aparece aquí, con el estado de pago siempre actualizado.",
      },
    },
    {
      element: TABLE,
      popover: {
        title: "Todos los recaudos",
        description:
          "Cada recaudo con su detalle y estado. Si su comercio tiene integración con el ERP, esta tabla refleja lo que existe en el ERP.",
      },
    },
    {
      element: FILTERS,
      popover: {
        title: "Filtre por estado o fecha",
        description:
          "Revise lo pendiente, lo vencido o lo ya pagado en el periodo que necesite.",
      },
    },
    {
      element: ACTIONS,
      popover: {
        title: "Cree recaudos",
        description:
          "Cree recaudos uno a uno o en lote con la plantilla de Excel. Use la carga masiva solo si no tiene integración con el ERP: con la integración activa, los recaudos se sincronizan automáticamente.",
      },
    },
  ],
  conciliacion: [
    {
      popover: {
        title: "Conciliación",
        description:
          "Cada vez que recibe un abono, aquí queda el detalle: qué facturas se pagaron, cuánto se descontó y cuánto llegó a su cuenta.",
      },
    },
    {
      element: FILTERS,
      popover: {
        title: "Busque un abono",
        description: "Filtre por fecha para encontrar un abono puntual.",
      },
    },
    {
      element: ACTIONS,
      popover: {
        title: "Reporte de pagos",
        description:
          "Al descargarlo, elija bien el tipo: «Fecha de Pago» filtra por el día en que el inquilino pagó; «Fecha de Abono», por el día en que el dinero se abonó a su cuenta. Para conciliar contra el extracto bancario, use Fecha de Abono.",
      },
    },
    {
      element: TABLE,
      popover: {
        title: "Ábralo para ver el detalle",
        description:
          "Entre a un abono para ver las facturas que lo componen y los descuentos aplicados.",
      },
    },
  ],
  whatsapp: [
    {
      popover: {
        title: "Recordatorios por WhatsApp",
        description:
          "Se envían mensajes automáticos a los inquilinos para que paguen a tiempo, sin necesidad de recordatorios manuales.",
      },
    },
    {
      element: ACTIONS,
      popover: {
        title: "Actívelos cuando quiera",
        description:
          "Active o desactive los recordatorios y defina en qué momentos se envían.",
      },
    },
  ],
  pagos: [
    {
      popover: {
        title: "Pagos a propietarios",
        description:
          "Desde aquí se gira el dinero recaudado a los propietarios y se lleva el control del saldo.",
      },
    },
    {
      element: SUMMARY,
      popover: {
        title: "Saldo",
        description:
          "Cuánto hay disponible para dispersar y cuánto está comprometido en lotes en curso.",
      },
    },
    {
      element: ACTIONS,
      popover: {
        title: "Disperse y concilie",
        description:
          "Descargue el reporte de pagos del periodo. Con permisos de dispersión, aquí también se cargan los lotes de pago.",
      },
    },
    {
      element: '[data-tour="payouts-tab-movimientos"]',
      popover: {
        title: "Movimientos",
        description:
          "Todo lo que entra y sale del saldo: liquidaciones, dispersiones, retiros y sus reembolsos, ordenados por fecha.",
      },
    },
    {
      element: '[data-tour="payouts-tab-lotes"]',
      popover: {
        title: "Lotes de dispersión",
        description:
          "Cada archivo de pagos cargado queda como un lote. Aquí se sigue su estado y se consultan los pagos que lo componen.",
      },
    },
    {
      element: '[data-tour="payouts-tab-detalle"]',
      popover: {
        title: "Detalle de dispersiones",
        description:
          "Pago por pago, sin importar el lote: a quién se le giró, cuánto y en qué estado quedó. Aquí se descarga el comprobante de cada uno.",
      },
    },
    {
      element: '[data-tour="payouts-tab-retiros"]',
      popover: {
        title: "Retiros a cuenta propia",
        description:
          "El historial del dinero movido a su propia cuenta, con monto y comisión.",
      },
    },
  ],
  facturacion: [
    {
      popover: {
        title: "Su facturación con Palomma",
        description:
          "Aquí encuentra lo que Palomma le cobra por el servicio, mes a mes.",
      },
    },
    {
      element: TABLE,
      popover: {
        title: "Cuentas de cobro",
        description:
          "Revise el detalle de cada mes. Si alguna cuenta está pendiente, páguela con el botón «Pagar» de su fila.",
      },
    },
  ],
  suscripciones: [
    {
      popover: {
        title: "Palomma Plus",
        description:
          "Administre las suscripciones de los inquilinos y los beneficios que recibe su comercio por cada suscriptor.",
      },
    },
    {
      element: '[data-tour="subscriptions-tab-suscriptores"]',
      popover: {
        title: "Suscriptores",
        description:
          "Los inquilinos suscritos, desde cuándo y el estado de cada suscripción.",
      },
    },
    {
      element: '[data-tour="subscriptions-tab-invitados"]',
      popover: {
        title: "Invitados",
        description:
          "Por cada suscripción activa ganas cupos para darles los beneficios de Palomma Plus a propietarios y colaboradores. Aquí se administran esas invitaciones.",
      },
    },
    {
      element: ACTIONS,
      popover: {
        title: "Invitados y reportes",
        description:
          "Descargue el «Reporte de Pagos» con el detalle de los cobros de las suscripciones y, si tiene permisos, asigne los cupos con «Agregar invitados».",
      },
    },
  ],
};
